import Color from 'color';
import styled from "styled-components";

import { BLACK, RED, WHITE, YELLOW } from '../../../theme/colors';
import { MAX_WIDTH, SIZE, gutter, margin, mediaQuery, pixelate, unit } from '../../../theme/metrics';
import { typeBodyMd, typeBodySm, typeHeadingLg, typeInput, typeLabelMd } from '../../../theme/typography';

export const StyledContainer = styled.div`
  background-color: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.primary};
	padding-top: ${unit(10)};
	padding-bottom: ${unit(10)};
	border-bottom: 1px solid ${({ theme }) => theme.alternative};
  
	${mediaQuery[SIZE.large]} {
		padding-top: ${unit(15)};
		padding-bottom: ${unit(15)};
	}
`;

export const StyledContent = styled.div`
  max-width: ${pixelate(MAX_WIDTH)};
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  display: grid;
  padding-left: ${margin(SIZE.small)};
	padding-right: ${margin(SIZE.small)};
  column-gap: ${gutter(SIZE.small)};
  grid-auto-flow: dense;
  grid-template-rows: auto;
  grid-template-columns: repeat(12, [col-start] 1fr);
	row-gap: ${unit(5)};
  
  ${mediaQuery[SIZE.medium]} {
    padding-left: ${margin(SIZE.medium)};
    padding-right: ${margin(SIZE.medium)};
    column-gap: ${gutter(SIZE.medium)};
  }

  ${mediaQuery[SIZE.large]} {
    padding-left: ${margin(SIZE.large)};
    padding-right: ${margin(SIZE.large)};
    column-gap: ${gutter(SIZE.large)};
		row-gap: ${unit(10)};
  }

  ${mediaQuery[SIZE.xlarge]} {
    padding-left: ${margin(SIZE.xlarge)};
    padding-right: ${margin(SIZE.xlarge)};
    column-gap: ${gutter(SIZE.xlarge)};
  }

  ${mediaQuery[SIZE.max]} {
    padding-left: ${margin(SIZE.max)};
    padding-right: ${margin(SIZE.max)};
    column-gap: ${gutter(SIZE.max)};
  }
`;


export const Header = styled.div`
	grid-column: span 12;

	${mediaQuery[SIZE.medium]} {
		grid-column: span 6;
	}

	${mediaQuery[SIZE.large]} {
		grid-column: span 5;
	}
`;

export const Title = styled.h2`
	${typeHeadingLg};

	${mediaQuery[SIZE.large]} {
		${typeHeadingLg};
	}
`;

export const Description = styled.div`
	margin-top: ${unit(2)};

	${mediaQuery[SIZE.large]} {
		margin-top: ${unit(4)};
	}

	color: ${Color(WHITE).alpha(0.7).string()};

	h1, h2, h3, h4, h5{
		${typeLabelMd};
		color: ${YELLOW};
	}

	a {
		color: ${Color(WHITE).alpha(0.7).string()};
		white-space: nowrap;
	}
`;

export const Body = styled.div`
	grid-column: span 12;

	${mediaQuery[SIZE.medium]} {
		grid-column: span 6;
	}

	${mediaQuery[SIZE.large]} {
		grid-column: 8 / span 5;
	}

	.checkbox{

		label{
			display: flex;

			input{
				width: ${unit(3)};
				height: ${unit(3)};
				border: 1px solid ${Color(WHITE).alpha(0.3).string()};
				flex: 0 0 ${unit(3)};
				margin-right: ${unit(2)};
				background-color: transparent;
			}
		}
	}
`;

export const ContactFormWrapper = styled.form`
	max-width: ${unit(58.75)};
`;

export const FieldContainer = styled.div`
	position: relative;

	${({ fieldType }) => fieldType === "checkbox" ? `
		display: flex;
		flex-direction: row-reverse;

		input{
			width: ${unit(3)};
			height: ${unit(3)};
			border: 1px solid ${Color(WHITE).alpha(0.3).string()};
			margin-right: ${unit(2)};
			background-color: transparent;
			flex: 0 0 ${unit(3)};
		}

		label{
			flex: 1;
		}

	` : `none`};

	&:not(:first-child) {
		margin-top: ${unit(2)};
	}

	label{
		display: ${({ fieldType }) => fieldType === "checkbox" ? "block" : "none"};
	}
`;

export const Asterisk = styled.svg`
	position: absolute;
	top: ${unit(1)};
	right: ${unit(1)};
`;

export const Textarea = styled.textarea`
	margin-top: ${unit(3)};
	margin-bottom: ${unit(4.5)};
`;

export const Checkbox = styled.div`
	flex: 0 0 ${unit(3)};
	display: flex;
	align-items: center;
	justify-content: center;
	height: ${unit(3)};
	border: 1px solid ${Color(WHITE).alpha(0.3).string()};
	transition: border-color 380ms cubic-bezier(0.22, 1, 0.36, 1);
`;

export const CheckboxLabel = styled.div`
	margin-left: ${unit(5)};
`;

export const BrochureContainer = styled.div`
	display: flex;
	cursor: pointer;

	${mediaQuery[SIZE.large]} {
		&:hover ${Checkbox} {
			border-color: ${WHITE};
		}
	}
`;

export const MarketingContainer = styled.div`
	display: flex;
	cursor: pointer;
	margin-top: ${unit(5)};
	margin-bottom: ${unit(5)};

	${mediaQuery[SIZE.large]} {
		&:hover ${Checkbox} {
			border-color: ${WHITE};
		}
	}
`;

export const Note = styled.div`
	${typeBodySm};
	color: ${Color(WHITE).alpha(0.6).string()};

	margin-top: ${unit(5)};

	a{
		color: ${Color(WHITE).alpha(0.6).string()};
	}
`;

export const SubmitButton = styled.button`
	${typeLabelMd};
	margin-top: ${unit(5)};
	background-color: ${WHITE};
	color: ${props => props.theme.background_dark};
	padding: ${unit(2)};

	&:disabled{
		opacity: 0.5;
	}
`;

export const MessageError = styled.p`
	${typeBodySm};
	width: 100%;
	margin-top: ${unit(3)};
	color: ${WHITE};
`;

export const MessageSuccess = styled.p`
	${typeBodyMd};
	color: ${WHITE};
`;

export const SuccessWrapper = styled.div`
	${typeBodySm};
	width: 100%;
	margin-top: ${unit(3)};
	padding-top: ${unit(3)};
	padding-bottom: ${unit(3)};
	padding-left: ${unit(2)};
	padding-right: ${unit(2)};
	background-color: ${YELLOW};
	color: ${BLACK};
	text-align: center;
`;

export const ErrorWrapper = styled.div`
	${typeBodySm};
	width: 100%;
	margin-top: ${unit(3)};
	padding-top: ${unit(3)};
	padding-bottom: ${unit(3)};
	color: ${WHITE};
`;

export const FieldError = styled.div`
	${typeBodySm};
	width: 100%;
	padding-top: ${unit(1.5)};
	padding-bottom: ${unit(1.5)};
	padding-left: ${unit(3)};
	padding-right: ${unit(3)};
	background-color: ${RED};
	text-align: center;
`;

export const SelectWrapper = styled.div`
	position: relative;
	width: 100%;
	margin-top: ${unit(3)};
	border-radius: 0;
`;

export const InputSelect = styled.select`
	${typeInput};
	width: 100%;
	height: ${unit(7)};
	padding-left: ${unit(3)};
	background-color: transparent;
	color: ${WHITE};
	appearance: none;
	outline: none;
	cursor: pointer;
	border: 1px solid ${Color(WHITE).alpha(0.3).string()};
	transition: border-color 380ms cubic-bezier(0.22, 1, 0.36, 1);

	option{
		color: initial;
	}

	${mediaQuery[SIZE.large]} {
		&:hover {
			border-color: ${WHITE};
		}

		&:focus {
			border-color: ${props => props.theme.secondary};
		}
	}
`;

export const InputOption = styled.option`
	color: #000;

	&:disabled {
		color: gray;
	}
`;
